import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { getProfileAssetUrl } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  IndianRupee,
  CreditCard,
  ClipboardList,
  User,
  Camera,
  Edit,
  Save,
  Send,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TeacherProfile = ({ editMode = false }) => {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(null);
  const [resendingCreds, setResendingCreds] = useState(false);
  const photoInputRef = React.useRef(null);

  const isAdmin = user?.role_slug === 'super_admin' || user?.role_slug === 'school_admin';

  const handleResendCredentials = async () => {
    const uid = profile?.teacher?.user_id;
    if (!uid) return;
    setResendingCreds(true);
    try {
      const res = await api.post('/auth/resend-credentials', { user_id: uid });
      const msg = res?.data?.message || 'New login credentials have been emailed to the user.';
      toast({ title: 'Credentials sent', description: msg });
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.message || 'Could not send credentials', variant: 'destructive' });
    } finally {
      setResendingCreds(false);
    }
  };

  useEffect(() => {
    if (id) fetchProfile();
  }, [id, user?.school_id]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => {
      if (id) fetchProfile();
    };
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [id, user?.school_id]);

  const fetchProfile = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const schoolId = user?.school_id;
      const response = await api.get(`teachers/${id}/profile`, {
        params: {
          ...(schoolId ? { school_id: schoolId } : {}),
          ...(academicYearId ? { academic_year_id: academicYearId } : {}),
        },
      });
      const data = response.data.data;
      setProfile(data);
      if (editMode && data?.teacher) {
        const t = data.teacher;
        setFormData({
          first_name: t.first_name || '',
          last_name: t.last_name || '',
          phone: t.phone || '',
          designation: t.designation || '',
          department: t.department || '',
          qualification: t.qualification || '',
          experience_years: t.experience_years != null ? String(t.experience_years) : '',
          address: t.address || '',
          salary: t.salary != null ? String(t.salary) : '',
          status: t.status || 'active',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to load teacher profile',
        variant: 'destructive',
      });
      navigate('/teachers');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData || !id) return;
    setSaving(true);
    try {
      await api.put(`teachers/${id}`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone || null,
        designation: formData.designation || null,
        department: formData.department || null,
        qualification: formData.qualification || null,
        experience_years: formData.experience_years ? parseFloat(formData.experience_years) : null,
        address: formData.address || null,
        salary: formData.salary ? parseFloat(formData.salary) : null,
        status: formData.status || null,
      });
      toast({ title: 'Success', description: 'Teacher updated successfully' });
      navigate(`/teachers/${id}`);
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to update teacher',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return d;
    }
  };

  const formatCurrency = (n) => {
    const num = Number(n);
    if (isNaN(num)) return '—';
    return `₹${num.toLocaleString('en-IN')}`;
  };

  const formatMonthYear = (month, year) => {
    if (!month || !year) return '—';
    return `${MONTH_NAMES[Number(month)] || month} ${year}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-600 border-t-blue-500" />
      </div>
    );
  }

  if (!profile) return null;

  const { teacher, salary_payments = [], salary_summary = {}, attendance_summary = {} } = profile;
  const fullName = [teacher.first_name, teacher.last_name].filter(Boolean).join(' ') || 'Teacher';
  const initials = (teacher.first_name?.[0] || '') + (teacher.last_name?.[0] || '') || '?';
  const avatarUrl = getProfileAssetUrl(teacher.avatar);

  const handlePhotoClick = () => {
    if (uploadingPhoto) return;
    photoInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please choose an image (JPG, PNG, GIF, WebP).', variant: 'destructive' });
      e.target.value = '';
      return;
    }
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await api.post(`teachers/${id}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newAvatar = res.data?.data?.avatar;
      if (newAvatar) {
        setProfile((prev) => prev ? { ...prev, teacher: { ...prev.teacher, avatar: newAvatar } } : null);
        toast({ title: 'Photo updated', description: 'Profile photo has been saved.' });
      }
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err.response?.data?.message || 'Could not upload photo.',
        variant: 'destructive',
      });
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="teacher-profile-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <Button
          variant="ghost"
          className="text-slate-400 hover:text-white -ml-2"
          onClick={() => navigate(editMode ? `/teachers/${id}` : '/teachers')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> {editMode ? 'Cancel (view profile)' : 'Back to Teachers'}
        </Button>
        {!editMode && (
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                variant="outline"
                className="border-amber-600 text-amber-400 hover:bg-amber-600 hover:text-white"
                onClick={handleResendCredentials}
                disabled={resendingCreds}
              >
                <Send className="w-4 h-4 mr-2" /> {resendingCreds ? 'Sending...' : 'Resend Credentials'}
              </Button>
            )}
            <Button
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
              onClick={() => navigate(`/teachers/${id}/edit`)}
            >
              <Edit className="w-4 h-4 mr-2" /> Edit profile
            </Button>
          </div>
        )}
      </div>

      <Card className="bg-slate-800 border-slate-700 overflow-hidden">
        <div className="bg-gradient-to-r from-green-800/80 to-slate-800 h-24" />
        <CardContent className="relative px-6 pb-6">
          <div className="flex flex-col sm:flex-row gap-6 -mt-14">
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={handlePhotoChange}
            />
            <button
              type="button"
              onClick={handlePhotoClick}
              disabled={uploadingPhoto}
              className="relative h-28 w-28 shrink-0 rounded-xl border-4 border-slate-800 shadow-xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-slate-800 hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              <Avatar className="h-full w-full rounded-xl">
                <AvatarImage src={avatarUrl} alt={fullName} className="object-cover" />
                <AvatarFallback className="rounded-xl bg-green-600/80 text-xl text-white">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity rounded-xl">
                <Camera className="w-8 h-8 text-white" />
              </span>
              {uploadingPhoto && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-xl">
                  <span className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
                </span>
              )}
            </button>
            <div className="flex-1 pt-2">
              {editMode && formData ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-400">First name</Label>
                      <Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
                    </div>
                    <div>
                      <Label className="text-slate-400">Last name</Label>
                      <Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="bg-slate-700 text-slate-300">
                      {teacher.employee_id} (read-only)
                    </Badge>
                    {teacher.email && (
                      <span className="text-sm text-slate-500 flex items-center gap-1">
                        <Mail className="w-4 h-4" /> {teacher.email}
                      </span>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-400">Phone</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl font-bold text-white">{fullName}</h1>
                    <Badge variant="secondary" className="bg-slate-700 text-slate-300">
                      {teacher.employee_id}
                    </Badge>
                    {teacher.status && (
                      <Badge variant={teacher.status === 'active' ? 'default' : 'outline'}>
                        {teacher.status}
                      </Badge>
                    )}
                  </div>
                  <p className="text-slate-400 mt-1">
                    {teacher.designation || '—'} {teacher.department ? `• ${teacher.department}` : ''}
                  </p>
                  <div className="flex flex-wrap gap-4 mt-4 text-sm text-slate-400">
                    {teacher.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4 text-slate-500" /> {teacher.email}
                      </span>
                    )}
                    {teacher.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-4 h-4 text-slate-500" /> {teacher.phone}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {editMode && formData ? (
            <form onSubmit={handleSave} className="space-y-6">
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <User className="w-5 h-5" /> Edit Personal & Professional Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400">Designation</Label>
                      <Input value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
                    </div>
                    <div>
                      <Label className="text-slate-400">Department</Label>
                      <Input value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400">Qualification</Label>
                      <Input value={formData.qualification} onChange={(e) => setFormData({ ...formData, qualification: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
                    </div>
                    <div>
                      <Label className="text-slate-400">Experience (years)</Label>
                      <Input type="number" step="0.5" value={formData.experience_years} onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400">Base salary (₹)</Label>
                      <Input type="number" value={formData.salary} onChange={(e) => setFormData({ ...formData, salary: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
                    </div>
                    <div>
                      <Label className="text-slate-400">Status</Label>
                      <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-700 border-slate-600">
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-400">Address</Label>
                    <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="bg-slate-700 border-slate-600 text-white" />
                  </div>
                </CardContent>
              </Card>
              <div className="flex gap-3">
                <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                  {saving ? <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent inline-block" /> : <Save className="w-4 h-4 mr-2" />}
                  {saving ? 'Saving...' : 'Save changes'}
                </Button>
                <Button type="button" variant="outline" className="border-slate-600 text-slate-300" onClick={() => navigate(`/teachers/${id}`)}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5" /> Personal & Professional Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <DetailRow label="Date of birth" value={formatDate(teacher.date_of_birth)} />
              <DetailRow label="Gender" value={teacher.gender || '—'} />
              <DetailRow label="Designation" value={teacher.designation || '—'} />
              <DetailRow label="Department" value={teacher.department || '—'} />
              <DetailRow label="Qualification" value={teacher.qualification || '—'} />
              <DetailRow label="Experience (years)" value={teacher.experience_years ?? '—'} />
              <DetailRow label="Joining date" value={formatDate(teacher.joining_date)} />
              <DetailRow label="Base salary" value={teacher.salary != null ? formatCurrency(teacher.salary) : '—'} />
              {teacher.address && (
                <div className="flex gap-2 pt-1">
                  <MapPin className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                  <span className="text-slate-300">{teacher.address}</span>
                </div>
              )}
            </CardContent>
          </Card>
          )}

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5" /> Salary Payments
              </CardTitle>
              <p className="text-slate-400 text-sm">
                Total paid: {formatCurrency(salary_summary.total_paid)} ({salary_summary.payment_count || 0} payment(s))
              </p>
            </CardHeader>
            <CardContent>
              {salary_payments.length === 0 ? (
                <p className="text-slate-500 text-sm py-4">No salary payments recorded yet.</p>
              ) : (
                <div className="rounded-lg border border-slate-700 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-700/50 text-slate-300 text-left">
                        <th className="px-4 py-2 font-medium">Month / Year</th>
                        <th className="px-4 py-2 font-medium">Basic</th>
                        <th className="px-4 py-2 font-medium">Net</th>
                        <th className="px-4 py-2 font-medium">Payment date</th>
                        <th className="px-4 py-2 font-medium">Method</th>
                        <th className="px-4 py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salary_payments.map((p) => (
                        <tr key={p.id} className="border-t border-slate-700 text-slate-300">
                          <td className="px-4 py-2">{formatMonthYear(p.month, p.year)}</td>
                          <td className="px-4 py-2">{formatCurrency(p.basic_salary)}</td>
                          <td className="px-4 py-2 font-medium text-white">
                            {formatCurrency(p.net_salary)}
                          </td>
                          <td className="px-4 py-2">{formatDate(p.payment_date)}</td>
                          <td className="px-4 py-2">{p.payment_method || '—'}</td>
                          <td className="px-4 py-2">
                            <Badge variant={p.status === 'paid' ? 'default' : 'secondary'} className="text-xs">
                              {p.status || '—'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <ClipboardList className="w-5 h-5" /> Attendance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-400">Average attendance</span>
                  <span className="text-white font-semibold">
                    {attendance_summary.average_percent ?? 0}%
                  </span>
                </div>
                <Progress value={attendance_summary.average_percent ?? 0} className="h-2" />
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <StatPill label="Present" value={attendance_summary.present ?? 0} />
                <StatPill label="Absent" value={attendance_summary.absent ?? 0} />
                <StatPill label="Late" value={attendance_summary.late ?? 0} />
                <StatPill label="Total days" value={attendance_summary.total_days ?? 0} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <IndianRupee className="w-5 h-5" /> Salary Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-white">
                {formatCurrency(salary_summary.total_paid)}
              </p>
              <p className="text-slate-400 text-sm mt-1">
                {salary_summary.payment_count ?? 0} payment(s) recorded
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between py-1">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-300">{value}</span>
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="bg-slate-700/50 rounded-lg px-3 py-2">
      <p className="text-slate-400 text-xs">{label}</p>
      <p className="text-white font-semibold">{value}</p>
    </div>
  );
}

export default TeacherProfile;
